// src/components/ui-components/ExcelUploadComponent.js

import React, { useState, useRef } from 'react';
import { Button, Flex, IconButton } from '@radix-ui/themes';
import { FiUpload, FiDownload } from 'react-icons/fi';
import axiosInstance from '../../config/axiosConfig';
import ConfirmationDialogue from './ConfirmationDialogue';

const ExcelUploadComponent = ({ 
  type, // 'gateway' or 'sensor'
  onUploadSuccess, 
  showToast 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);

  // Configuration based on type
  const getConfig = () => {
    switch (type) {
      case 'gateway':
        return {
          uploadEndpoint: '/gateway/upload-gateways',
          templateEndpoint: '/gateway/download-template',
          templateFileName: 'Gateway_Import_Template.xlsx',
          resultFileName: 'Gateway_Import_Result.xlsx',
          displayName: 'Gateway'
        };
      case 'sensor':
        return {
          uploadEndpoint: '/sensor/upload-sensors',
          templateEndpoint: '/sensor/download-template',
          templateFileName: 'Sensor_Import_Template.xlsx',
          resultFileName: 'Sensor_Import_Result.xlsx',
          displayName: 'Sensor'
        };
      default:
        return null;
    }
  };

  const config = getConfig();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Basic file validation
      if (file.name.toLowerCase().endsWith('.xlsx')) {
        setSelectedFile(file);
        setShowConfirmation(true);
      } else {
        showToast(`Please select a valid .xlsx file for ${config.displayName} upload.`, 'error');
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handleUploadConfirm = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setShowConfirmation(false);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axiosInstance.post(config.uploadEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob', // Important for file download
      });

      // Auto-download the result file
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = config.resultFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      // Show success message
      showToast(`${config.displayName} Excel file uploaded successfully! Result file downloaded.`);
      
      // Trigger refresh of parent component
      if (onUploadSuccess) {
        onUploadSuccess();
      }

    } catch (error) {
      console.error(`Error uploading ${config.displayName} Excel:`, error);
      
      let errorMessage = `Failed to upload ${config.displayName} Excel file.`;
      
      if (error.response && error.response.data) {
        // Handle blob error response
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.Message || errorMessage;
          } catch (parseError) {
            console.error('Error parsing error response:', parseError);
          }
        } else if (error.response.data.Message) {
          errorMessage = error.response.data.Message;
        }
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadCancel = () => {
    setShowConfirmation(false);
    setSelectedFile(null);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTemplateDownload = async () => {
    setIsDownloadingTemplate(true);

    try {
      const response = await axiosInstance.get(config.templateEndpoint, {
        responseType: 'blob',
      });

      // Auto-download the template file
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = config.templateFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      showToast(`${config.displayName} template downloaded successfully!`);

    } catch (error) {
      console.error(`Error downloading ${config.displayName} template:`, error);
      showToast(`Failed to download ${config.displayName} template.`, 'error');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (!config) {
    console.error('Invalid type provided to ExcelUploadComponent');
    return null;
  }

  return (
    <>
      <Flex gap="2" align="center">
        {/* Template Download Button */}
        <IconButton
          variant="soft"
          color="blue"
          onClick={handleTemplateDownload}
          disabled={isDownloadingTemplate}
          title={`Download ${config.displayName} Template`}
        >
          <FiDownload size={16} />
        </IconButton>

        {/* Upload Button */}
        <IconButton
          variant="soft"
          color="green"
          onClick={triggerFileSelect}
          disabled={isUploading}
          title={`Upload ${config.displayName} Excel`}
        >
          <FiUpload size={16} />
        </IconButton>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </Flex>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <ConfirmationDialogue
          Name={selectedFile?.name || 'Excel file'}
          Type="Upload"
          Action="upload this Excel file"
          onCancel={handleUploadCancel}
          onConfirm={handleUploadConfirm}
        />
      )}
    </>
  );
};

export default ExcelUploadComponent;